import {
    Box,
    Typography,
    Input,
    Option,
    Select,
    IconButton as JoyIconButton,
} from "@mui/joy";
import type { ReactNode } from "react";
import { Palette } from "lucide-react";

import { Button } from "../../../../shared";
import { MaterialProductSelect } from "../widgets";
import { MATERIAL_ROW, materialRowDividerSx } from "./materialRowLayout";
import { FormulaQuantityIndicator } from "./FormulaQuantityIndicator";
import {
    formatSupplierUnitPrice,
    formatUnitPriceRange,
} from "../../utils/materialPriceDisplay";
import { findOptionForProductId } from "../../utils/materialColorSelection";
import type { MaterialCardItemProps, MaterialCardProps } from "./types";
import type { MaterialFormulaFlag } from "../../types";

export type { MaterialCardItemProps } from "./types";
export { FormulaTooltipContent } from "./FormulaTooltipContent";

function formatSpecs(item: MaterialCardItemProps) {
    const parts = [item.variant, item.detail].filter(Boolean);
    if (item.size && item.lineLabel && !item.lineLabel.includes(item.size)) {
        parts.push(item.size);
    }
    return parts.length > 0 ? parts.join(" · ") : null;
}

function resolveUnitPriceDisplay(item: MaterialCardItemProps): string | null {
    const hasSupplier = !!(item.supplierId || item.supplierName);
    const supplierPrice = formatSupplierUnitPrice(item.supplierUnitPrice);
    if (supplierPrice) return supplierPrice;

    if (hasSupplier) {
        return null;
    }

    if (item.selectedProductId) {
        const selectedOption = findOptionForProductId(
            item.availableProducts,
            item.selectedProductId,
        );
        const variant = selectedOption?.Variants?.find(
            (entry) => entry.ProductId === item.selectedProductId,
        );
        if (variant) {
            return formatUnitPriceRange(variant.MinPrice, variant.MaxPrice);
        }
        if (selectedOption) {
            return formatUnitPriceRange(
                selectedOption.MinPrice,
                selectedOption.MaxPrice,
            );
        }
    }

    if (item.priceRange) {
        return formatUnitPriceRange(item.priceRange.Min, item.priceRange.Max);
    }

    return null;
}

const METRIC_LABEL_HEIGHT = 18;
const UNIT_PRICE_WIDTH = 136;

const quantityStepperSx = {
    display: "inline-flex",
    alignItems: "stretch",
    height: 32,
    borderRadius: "4px",
    border: "1px solid",
    borderColor: "divider",
    bgcolor: "background.surface",
    overflow: "hidden",
} as const;

const quantityStepperButtonSx = {
    minWidth: 32,
    width: 32,
    height: 32,
    minHeight: 32,
    p: 0,
    borderRadius: 0,
    border: "none",
    boxShadow: "none",
    flexShrink: 0,
    color: "text.primary",
    bgcolor: "transparent",
    "--IconButton-radius": "0px",
    "&:hover": {
        bgcolor: "background.level1",
    },
} as const;

function QuantityField({
    value,
    onChange,
    hasFormula,
    formulaActive,
    quantityOverridden,
    formula,
    onResetToFormula,
}: {
    value: number;
    onChange: (next: number) => void;
    hasFormula?: boolean;
    formulaActive?: boolean;
    quantityOverridden?: boolean;
    formula?: MaterialFormulaFlag;
    onResetToFormula?: () => void;
}) {
    const setQuantity = (next: number) => {
        onChange(Math.max(0, next));
    };

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: { xs: "flex-start", md: "flex-end" },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    width: "100%",
                    justifyContent: "space-between",
                    height: METRIC_LABEL_HEIGHT,
                    flexShrink: 0,
                }}
            >
                {hasFormula && (
                    <FormulaQuantityIndicator
                        formula={formula}
                        formulaActive={formulaActive}
                        quantityOverridden={quantityOverridden}
                        onResetToFormula={onResetToFormula}
                    />
                )}
            </Box>
            <Box sx={quantityStepperSx}>
                <JoyIconButton
                    size="sm"
                    variant="plain"
                    color="neutral"
                    onClick={() => setQuantity(value - 1)}
                    sx={{
                        ...quantityStepperButtonSx,
                        borderRight: "1px solid",
                        borderColor: "divider",
                    }}
                >
                    −
                </JoyIconButton>
                <Input
                    type="number"
                    size="sm"
                    value={value}
                    slotProps={{
                        input: {
                            min: 0,
                            sx: {
                                textAlign: "center",
                                fontVariantNumeric: "tabular-nums",
                                MozAppearance: "textfield",
                                "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button":
                                    {
                                        WebkitAppearance: "none",
                                        margin: 0,
                                    },
                            },
                        },
                    }}
                    onChange={(e) => setQuantity(Number(e.target.value) || 0)}
                    sx={{
                        width: 72,
                        height: 32,
                        px: 0,
                        borderRadius: 0,
                        border: "none",
                        borderRight: "1px solid",
                        borderColor: "divider",
                        boxShadow: "none",
                        "--Input-minHeight": "32px",
                        "--Input-paddingInline": "8px",
                        bgcolor: "transparent",
                        "--Input-focusedHighlight": "transparent",
                        "&:hover": { bgcolor: "transparent" },
                        "&::before": { display: "none" },
                    }}
                />
                <JoyIconButton
                    size="sm"
                    variant="plain"
                    color="neutral"
                    onClick={() => setQuantity(value + 1)}
                    sx={quantityStepperButtonSx}
                >
                    +
                </JoyIconButton>
            </Box>
        </Box>
    );
}

function Metric({
    label,
    value,
    width,
}: {
    label: string;
    value: ReactNode;
    width?: number;
}) {
    return (
        <Box
            sx={{
                width: width ?? { md: 80 },
                minWidth: width ?? { md: 80 },
                flexShrink: 0,
                textAlign: { xs: "left", md: "right" },
            }}
        >
            <Typography
                level="body-xs"
                color="neutral"
                sx={{ lineHeight: 1.2, height: METRIC_LABEL_HEIGHT, mb: -0.5 }}
            >
                {label}
            </Typography>
            <Typography
                level="body-sm"
                sx={{
                    fontVariantNumeric: "tabular-nums",
                    lineHeight: 1.3,
                    height: 32,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: { xs: "flex-start", md: "flex-end" },
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                }}
            >
                {value}
            </Typography>
        </Box>
    );
}

export function MaterialCard({
    item,
    onDelete,
    onProductChange,
    onQuantityChange,
    onResetToFormula,
    onColorChange,
    productMultiSelect,
    productMultiValues,
    onProductMultiChange,
}: MaterialCardProps) {
    const showCategory = item.showCategoryInCard !== false && !item.lineLabel;
    const specs = formatSpecs(item);
    const unitPrice = resolveUnitPriceDisplay(item);
    const rowPadding = item.inGroup ? MATERIAL_ROW.nestedPx : MATERIAL_ROW.px;
    const catalogColors = item.colors ?? [];
    const showCatalogColor = catalogColors.length > 0;
    const showFreeTextColor = !!item.showColorInput && !showCatalogColor;

    return (
        <Box
            sx={{
                px: rowPadding,
                pt: 1.25,
                pb: 1.75,
                ...materialRowDividerSx,
                display: "grid",
                gridTemplateColumns: {
                    xs: "1fr",
                    md: onDelete
                        ? "minmax(0, 1fr) auto auto"
                        : "minmax(0, 1fr) auto",
                },
                gap: { xs: 1.25, md: 2.5 },
                alignItems: { md: item.lineLabel ? "flex-end" : "center" },
                "&:last-of-type": { borderBottom: "none" },
            }}
        >
            <Box sx={{ minWidth: 0 }}>
                {item.lineLabel && (
                    <Typography level="body-sm" sx={{ mb: 0.5 }}>
                        {item.lineLabel}
                    </Typography>
                )}
                {showCategory && (
                    <Typography
                        level="body-xs"
                        color="neutral"
                        sx={{ mb: 0.25 }}
                    >
                        {item.productName}
                    </Typography>
                )}
                <Box
                    sx={{
                        display: "flex",
                        gap: 1,
                        alignItems: "stretch",
                        flexWrap: { xs: "wrap", sm: "nowrap" },
                    }}
                >
                    <Box sx={{ flex: 1, minWidth: { xs: "100%", sm: 0 } }}>
                        <MaterialProductSelect
                            products={item.availableProducts}
                            value={item.selectedProductId}
                            onChange={(optionKey) =>
                                onProductChange?.(optionKey)
                            }
                            multiple={!!productMultiSelect}
                            multiValues={productMultiValues}
                            onMultiChange={onProductMultiChange}
                            maxMulti={5}
                        />
                    </Box>
                    {showCatalogColor && (
                        <Select
                            size="sm"
                            placeholder="Color"
                            value={item.color || null}
                            startDecorator={<Palette size={14} />}
                            onChange={(_, next) =>
                                onColorChange?.((next as string) ?? "")
                            }
                            sx={{
                                width: { xs: "100%", sm: 180 },
                                flexShrink: 0,
                                bgcolor: "background.surface",
                                borderRadius: "4px",
                                boxShadow: "none",
                            }}
                        >
                            {catalogColors.map((colorName) => (
                                <Option key={colorName} value={colorName}>
                                    {colorName}
                                </Option>
                            ))}
                        </Select>
                    )}
                    {showFreeTextColor && (
                        <Input
                            size="sm"
                            placeholder="Shingle color"
                            value={item.color ?? ""}
                            startDecorator={<Palette size={14} />}
                            onChange={(e) => onColorChange?.(e.target.value)}
                            sx={{
                                width: { xs: "100%", sm: 180 },
                                flexShrink: 0,
                                bgcolor: "background.surface",
                                borderRadius: "4px",
                                boxShadow: "none",
                            }}
                        />
                    )}
                </Box>
                {specs && (
                    <Typography
                        level="body-xs"
                        color="neutral"
                        sx={{ mt: 0.5 }}
                    >
                        {specs}
                    </Typography>
                )}
            </Box>

            <Box
                sx={{
                    display: "flex",
                    gap: 2.5,
                    alignItems: "flex-end",
                    flexWrap: "wrap",
                }}
            >
                <QuantityField
                    value={item.quantity}
                    onChange={(next) =>
                        onQuantityChange && onQuantityChange(next)
                    }
                    hasFormula={item.hasFormula}
                    formulaActive={item.formulaActive}
                    quantityOverridden={item.quantityOverridden}
                    formula={item.formula}
                    onResetToFormula={onResetToFormula}
                />
                <Metric
                    label="Unit price"
                    value={unitPrice ? `$${unitPrice}` : "—"}
                    width={UNIT_PRICE_WIDTH}
                />
            </Box>

            {onDelete && (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: { xs: "flex-start", md: "flex-end" },
                        gridColumn: { xs: "1", md: "auto" },
                    }}
                >
                    <Button
                        variant="plain"
                        color="danger"
                        size="sm"
                        onClick={onDelete}
                        sx={{ minHeight: 28, fontSize: "sm" }}
                    >
                        Remove
                    </Button>
                </Box>
            )}
        </Box>
    );
}
